import { create } from 'zustand';
import { api } from '@/lib/api';
import { useGamificationStore } from '@/stores/gamification';
import { useNotificationStore } from '@/stores/notifications';
import type { User, AuthResponse, ApiResponse } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<{ success: boolean; mfa_required?: boolean; error?: string }>;
  register: (email: string, password: string, fullName: string, inviteCode?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password, mfaCode) => {
    try {
      const res = await api.post<ApiResponse<AuthResponse>>('/auth/login', { email, password, mfa_code: mfaCode });
      if (res.success && res.data) {
        api.setToken(res.data.access_token);
        localStorage.setItem('access_token', res.data.access_token);
        localStorage.setItem('refresh_token', res.data.refresh_token);
        set({ user: res.data.user, isAuthenticated: true });
        return { success: true };
      }
      if ((res as { mfa_required?: boolean }).mfa_required) return { success: false, mfa_required: true };
      return { success: false, error: res.error || 'Login failed' };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Login failed' };
    }
  },

  register: async (email, password, fullName, inviteCode) => {
    try {
      const res = await api.post<ApiResponse<AuthResponse>>('/auth/register', {
        email, password, full_name: fullName, invite_code: inviteCode,
      });
      if (res.success && res.data) {
        api.setToken(res.data.access_token);
        localStorage.setItem('access_token', res.data.access_token);
        localStorage.setItem('refresh_token', res.data.refresh_token);
        set({ user: res.data.user, isAuthenticated: true });
        return { success: true };
      }
      return { success: false, error: res.error || 'Registration failed' };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Registration failed' };
    }
  },

  logout: async () => {
    const uid = get().user?.id;
    const refreshToken = localStorage.getItem('refresh_token');
    if (uid) sessionStorage.removeItem(`gamification_seen_${uid}`);
    // The request captures the current authorization before local state is cleared.
    // Finish local logout immediately so a slow response cannot erase a later login.
    const revokeSession = api.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}).catch(() => undefined);
    api.setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    useGamificationStore.getState().reset();
    useNotificationStore.getState().reset();
    set({ user: null, isAuthenticated: false });
    await revokeSession;
  },

  loadUser: async () => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!token && !refreshToken) {
      set({ isLoading: false });
      return;
    }
    if (token) api.setToken(token);
    try {
      const res = await api.get<ApiResponse<User>>('/auth/me');
      if (res.success && res.data) {
        set({ user: res.data, isAuthenticated: true, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch {
      set({ isLoading: false });
    }
  },

  updateProfile: async (data) => {
    const res = await api.patch<ApiResponse<User>>('/users/profile/me', data);
    if (!res.success || !res.data) throw new Error(res.error || 'Profil konnte nicht gespeichert werden.');
    set({ user: res.data });
  },
}));
